/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { initAxios } from '../utils/axiosHelper';
import { parseErrorMsg } from '../utils/errorHelper';

// eslint-disable-next-line no-undef
export const ENTITY_SERVICE_BASE_URL = (
  process.env.REACT_APP_ENTITY_SERVICE_URL || '/entity-service'
).replace(/\/+$/, '');

/**
 * Every environment runs Entity Hub and Entity Screening on the `biteservices`
 * entity service. The REACT_APP_ENTITY_BACKEND flag that used to pin production
 * to the legacy `datahub` + `automations` pairing is gone, along with the dead
 * `legacy` half of the routing table it selected.
 */

interface Route {
  baseUrl: string;
  path: string;
}

export type RoutedOp =
  | 'entitySearch'
  | 'ownershipSearch'
  | 'abbvSearch'
  | 'runDatabaseScreening'
  | 'runEntityScreening'
  | 'getDatabaseScreeningStatus'
  | 'verifyWebsiteQuality'
  | 'verifyDomainAge'
  | 'verifyDomainReputation'
  | 'verifyEmail'
  | 'verifyPhone'
  | 'verifyBusinessLocation'
  | 'confirmBusinessLocationAspect'
  | 'verifyCbrRegistry'
  | 'runBusinessVerification'
  | 'bulkVerificationRuns'
  // Not called through EntityServiceAPI — bitedb/ai/ai.service.ts owns the
  // adverse-media call but must follow the same backend selection.
  | 'verifyAdverseMedia';

// The entity service was built as a drop-in for DatahubAPI + the automations
// screening routes; only the entity-service targets remain.
const ROUTES: Record<RoutedOp, Route> = {
  entitySearch: { baseUrl: ENTITY_SERVICE_BASE_URL, path: 'data/entity' },
  ownershipSearch: { baseUrl: ENTITY_SERVICE_BASE_URL, path: 'data/ownership' },
  abbvSearch: { baseUrl: ENTITY_SERVICE_BASE_URL, path: 'data/home-search' },
  runDatabaseScreening: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'screening/run',
  },
  runEntityScreening: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'screening/run',
  },
  getDatabaseScreeningStatus: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'screening/status',
  },
  verifyWebsiteQuality: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'business-checks/verify-website-quality',
  },
  verifyDomainAge: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'business-checks/verify-domain-age',
  },
  verifyDomainReputation: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'business-checks/verify-domain-reputation',
  },
  verifyEmail: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'business-checks/verify-email',
  },
  verifyPhone: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'business-checks/verify-phone',
  },
  verifyBusinessLocation: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'business-checks/verify-business-location',
  },
  confirmBusinessLocationAspect: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'business-checks/verify-business-location/confirm-aspect',
  },
  runBusinessVerification: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'business-verification/runs',
  },
  bulkVerificationRuns: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'business-verification/bulk-runs',
  },
  verifyCbrRegistry: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'business-checks/verify-cbr-registry',
  },
  verifyAdverseMedia: {
    baseUrl: ENTITY_SERVICE_BASE_URL,
    path: 'business-checks/verify-adverse-media',
  },
};

/**
 * Resolve the base URL + path for one entity operation against the selected
 * backend. Exported so callers that live outside this module (currently the
 * adverse-media check in bitedb/ai/ai.service.ts) route consistently instead of
 * pinning ENTITY_SERVICE_BASE_URL directly.
 */
export const routeFor = (op: RoutedOp): Route => ROUTES[op];

/**
 * A Bulk Verification Run call's answer. A refusal keeps the HTTP status and the
 * error body's `code`, `details` and `errors`, which `parseErrorMsg` drops, so
 * callers can tell a credit shortfall from unknown ids from a switched-off
 * environment. `status` is 0 when the service never answered.
 */
export interface BulkVerificationRunResponse {
  success: boolean;
  status: number;
  result: any;
  code?: string;
  details?: unknown;
  errors?: unknown;
}

interface BulkVerificationRunBody {
  databaseId: string;
  databaseEntityIds?: string[];
}

const bulkVerificationRunCall = async (
  send: (client: any, path: string) => Promise<any>,
): Promise<BulkVerificationRunResponse> => {
  const { baseUrl, path } = routeFor('bulkVerificationRuns');
  try {
    const client = await initAxios(baseUrl);
    if (!client) {
      return {
        success: false,
        status: 0,
        result: 'Error initializing api client',
      };
    }
    const resp = await send(client, path);
    return {
      success: resp.status >= 200 && resp.status < 300,
      status: resp.status,
      result: resp.data,
    };
  } catch (err: any) {
    console.error('EntityServiceAPI bulk verification run call failed', err);
    const data = err?.response?.data;
    return {
      success: false,
      status: (err?.response?.status as number | undefined) ?? 0,
      code: typeof data?.code === 'string' ? data.code : undefined,
      details: data?.details,
      errors: data?.errors,
      result: parseErrorMsg(err),
    };
  }
};

/**
 * Wrapper for the `biteservices` entity service.
 *
 * The browser reaches it same-origin through an nginx path proxy, so auth
 * tokens keep flowing through the existing axios interceptor in
 * {@link initAxios}. All methods return the shared client shape
 * `{ success: boolean, result: any }`.
 */
export const EntityServiceAPI = {
  /**
   *`DatahubAPI.entitySearch`
   */
  entitySearch: async (scoreParams: any) =>
    (await initAxios(routeFor('entitySearch').baseUrl))
      ?.post(
        routeFor('entitySearch').path,
        JSON.stringify({ scoreParams: scoreParams }),
      )
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.entitySearch failed', err);
        return { success: false, result: parseErrorMsg(err) };
      }),

  /**
   * Return every BITE Lens currently present in the entity index, in the same
   * order used by Entity Screening's lens picker.
   */
  getEntityScreeningLenses: async (): Promise<string[]> => {
    const response = await EntityServiceAPI.entitySearch({ agg: true });
    if (!response?.success) return [];

    const buckets =
      response.result?.result?.body?.aggregations?.entityAggs?.buckets;
    if (!Array.isArray(buckets)) return [];

    return Array.from(
      new Set(
        buckets
          .map((bucket: any) => bucket?.key)
          .filter(
            (lens: unknown): lens is string =>
              typeof lens === 'string' && lens.trim().length > 0,
          )
          .map((lens: string) => lens.trim()),
      ),
    );
  },

  /**
   * Beneficial-ownership / PEP search — replaces `DatahubAPI.ownershipSearch`.
   *
   * Queries the BOI/PEP index via the entity service (same scoreParams shape as
   * entitySearch); the Ownership tab renders the returned hits + aggs.
   */
  ownershipSearch: async (scoreParams: any) =>
    (await initAxios(routeFor('ownershipSearch').baseUrl))
      ?.post(
        routeFor('ownershipSearch').path,
        JSON.stringify({ scoreParams: scoreParams }),
      )
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.ownershipSearch failed', err);
        return { success: false, result: parseErrorMsg(err) };
      }),

  /**
   * Home-page global search — replaces `DatahubAPI.abbvSearch`.
   *
   * Buckets matches across entity and product fields by `BITE Lens`; the home
   * search box renders the returned `lensAgg` counts.
   */
  abbvSearch: async (scoreParams: any) =>
    (await initAxios(routeFor('abbvSearch').baseUrl))
      ?.post(routeFor('abbvSearch').path, JSON.stringify(scoreParams))
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.abbvSearch failed', err);
        return { success: false, result: parseErrorMsg(err) };
      }),
  /**
   * Run database screening through the entity service.
   *
   * The legacy Automations endpoint accepted `databaseIds` (one or many). The
   * entity service screens a single `databaseId` per call, so when more than one
   * id is supplied we fan out to `POST screening/run` once per id and return an
   * array of per-database results. A single id resolves to a single result.
   */
  runDatabaseScreening: async (
    request:
      | {
          databaseId?: string | number;
          databaseIds?: Array<string | number>;
          screenedBy?: string;
          screeningReason?: 'Manual Screen' | 'Automated Screening';
        }
      | string
      | number,
  ) => {
    const { baseUrl, path } = routeFor('runDatabaseScreening');
    const apiClient = await initAxios(baseUrl);
    if (!apiClient) {
      return { success: false, result: 'Error initializing api client' };
    }
    let ids: Array<string | number> = [];
    let rest: Record<string, any> = {};

    if (typeof request === 'string' || typeof request === 'number') {
      ids = [request];
    } else if (Array.isArray(request?.databaseIds)) {
      const { databaseIds, ...extra } = request;
      ids = databaseIds;
      rest = extra;
    } else if (request?.databaseId !== undefined) {
      const { databaseId, ...extra } = request;
      ids = [databaseId as string | number];
      rest = extra;
    }

    if (!ids.length) {
      return { success: false, result: 'No databaseId provided' };
    }

    try {
      const responses = await Promise.all(
        ids.map((databaseId) => {
          // The entity service validates `databaseId` as a non-empty string, so
          // coerce numeric ids from legacy callers before sending.
          const id = String(databaseId);
          return apiClient
            .post(path, {
              databaseId: id,
              screeningReason: 'Manual Screen',
              ...rest,
            })
            .then((resp) => ({
              databaseId: id,
              success: true,
              result: resp.data,
            }))
            .catch((err) => ({
              databaseId: id,
              success: false,
              result: parseErrorMsg(err),
            }));
        }),
      );

      // Preserve single-id ergonomics: return the lone result directly so
      // callers passing one id keep the existing `{ success, result }` shape.
      if (responses.length === 1) {
        return { success: responses[0].success, result: responses[0].result };
      }

      return {
        success: responses.every((r) => r.success),
        result: responses,
      };
    } catch (err) {
      console.error('EntityServiceAPI.runDatabaseScreening failed', err);
      return { success: false, result: parseErrorMsg(err) };
    }
  },

  /**
   * Scoped entity re-screen: publish only the given entities of one database to
   * the entity-service screening pipeline (`POST screening/run` with
   * `databaseEntityIds`). Replaces the automations `screenings/run/entity`
   * (single) and `screenings/run/select-entities` (retry) endpoints.
   */
  runEntityScreening: async (payload: {
    databaseId: string | number;
    databaseEntityIds: Array<string | number>;
    screenedBy?: string;
  }) => {
    const { baseUrl, path } = routeFor('runEntityScreening');
    const body = {
      databaseId: String(payload.databaseId),
      databaseEntityIds: payload.databaseEntityIds.map((id) => String(id)),
      screenedBy: payload.screenedBy,
      screeningReason: 'Manual Screen',
    };

    return (await initAxios(baseUrl))
      ?.post(path, body)
      .then((resp) => ({
        success: resp.status >= 200 && resp.status < 300,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.runEntityScreening failed', err);
        return { success: false, result: parseErrorMsg(err) };
      });
  },

  /**
   * Poll live database screening progress from the entity service.
   */
  getDatabaseScreeningStatus: async (databaseId: string | number) =>
    (await initAxios(routeFor('getDatabaseScreeningStatus').baseUrl))
      ?.get(routeFor('getDatabaseScreeningStatus').path, {
        params: { databaseId: databaseId },
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error(
          'EntityServiceAPI.getDatabaseScreeningStatus failed',
          err,
        );
        return { success: false, result: parseErrorMsg(err) };
      }),

  /**
   * Load the latest persisted BOI/PEP match snapshot for one DatabaseEntity.
   * Only the requested result window is returned; the UI drives page changes
   * so large ownership result sets are never loaded into browser memory at
   * once.
   */
  getEntityOwnershipMatches: async (
    databaseEntityId: string,
    page = 0,
    size = 100,
  ) => {
    const apiClient = await initAxios(ENTITY_SERVICE_BASE_URL);
    if (!apiClient) {
      return { success: false, result: 'Error initializing api client' };
    }

    try {
      const requestedPage = Math.max(0, page);
      const requestedSize = Math.max(1, size);
      const response = await apiClient.get(
        `screening/results/${databaseEntityId}/ownership-matches`,
        {
          params: { page: requestedPage, size: requestedSize },
        },
      );
      const payload = response.data;

      return {
        success: true,
        result: {
          total_hits: Number(payload?.total_hits ?? 0),
          source_total: Number(
            payload?.source_total ?? payload?.total_hits ?? 0,
          ),
          results: Array.isArray(payload?.results) ? payload.results : [],
        },
      };
    } catch (err) {
      console.error('EntityServiceAPI.getEntityOwnershipMatches failed', err);
      return { success: false, result: parseErrorMsg(err) };
    }
  },

  /**
   * User-triggered AI evaluation of the latest BOI/PEP rows already persisted
   * for one DatabaseEntity. This endpoint is deliberately separate from the
   * normal entity screening flow.
   */
  evaluateOwnership: async (payload: {
    databaseId: string;
    databaseEntityId: string;
    force?: boolean;
  }) => {
    return (await initAxios(ENTITY_SERVICE_BASE_URL))
      ?.post('screening/ownership/evaluate', JSON.stringify(payload), {
        timeout: 120000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.evaluateOwnership failed', err);
        return { success: false, result: parseErrorMsg(err) };
      });
  },

  /**
   * Export and Transshipment Risk: runs the entity service's risk agents
   * (adverse media, export control / diversion, financial, personnel) in
   * parallel and combines them into one verdict. Given record ids, each agent
   * saves its own result to the record, so a run that outlives this request
   * still lands. Live web searches, hence the long timeout.
   */
  evaluateEntityRisk: async (payload: {
    entityName: string;
    country?: string;
    address?: string;
    statedBusiness?: string;
    aliases?: string[];
    databaseId?: string;
    databaseEntityId?: string;
    entityId?: string;
    triggeredBy?: string;
  }) => {
    return (await initAxios(ENTITY_SERVICE_BASE_URL))
      ?.post('business-checks/evaluate-entity-risk', JSON.stringify(payload), {
        timeout: 300000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.evaluateEntityRisk failed', err);
        return { success: false, result: parseErrorMsg(err) };
      });
  },

  /**
   * Export a CSV of every entity in a database whose screening results changed
   * within a date range (matches added/removed/modified). The entity service
   * streams `text/csv`, so we request a Blob; on success `result` is the Blob
   * (the caller triggers the browser download). `from`/`to` are `yyyy-MM-dd`
   * and interpreted as inclusive whole UTC days server-side.
   */
  exportEntityChanges: async (params: {
    databaseId: string;
    from: string;
    to: string;
    includeBaseline?: boolean;
  }) => {
    return (await initAxios(ENTITY_SERVICE_BASE_URL))
      ?.get('screening/reports/changes', {
        params: params,
        responseType: 'blob',
        timeout: 300000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.exportEntityChanges failed', err);
        return { success: false, result: parseErrorMsg(err) };
      });
  },

  /**
   * Structured screening-change detail (JSON) for a set of selected entities
   * over a date range: per entity, the watchlist hits added / removed / modified
   * (with per-field before/after). The client renders the PDF/CSV from this.
   * `from`/`to` are `yyyy-MM-dd` (inclusive whole UTC days server-side).
   */
  getEntityChanges: async (payload: {
    databaseId: string;
    databaseEntityIds: string[];
    from: string;
    to: string;
  }) => {
    return (await initAxios(ENTITY_SERVICE_BASE_URL))
      ?.post('screening/reports/entity-changes', JSON.stringify(payload), {
        timeout: 120000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.getEntityChanges failed', err);
        return { success: false, result: parseErrorMsg(err) };
      });
  },

  // ---------------------------------------------------------------------------
  // Business verification checks
  // ---------------------------------------------------------------------------

  /**
   * One Verification Run: the service reads the record, runs every check its
   * details allow (except adverse media), saves each result to the record and
   * answers when all have finished. A refusal keeps its HTTP status and the
   * service's `code` so the page can say exactly why.
   */
  runBusinessVerification: async (payload: {
    databaseId: string;
    databaseEntityId: string;
  }) =>
    (await initAxios(routeFor('runBusinessVerification').baseUrl))
      ?.post(routeFor('runBusinessVerification').path, payload, {
        // The slowest check times out at 60s; leave room for the rest.
        timeout: 120000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        status: resp.status,
        code: undefined as string | undefined,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.runBusinessVerification failed', err);
        return {
          success: false,
          status: (err?.response?.status as number | undefined) ?? 0,
          code: err?.response?.data?.code as string | undefined,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * What a Bulk Verification Run would verify and cost, without reserving
   * credits. Omit `databaseEntityIds` for the whole database.
   */
  previewBulkVerificationRun: (body: BulkVerificationRunBody) =>
    bulkVerificationRunCall((client, path) =>
      // Planning pages through every row of a large database.
      client.post(`${path}/preview`, body, { timeout: 120000 }),
    ),

  startBulkVerificationRun: (body: BulkVerificationRunBody) =>
    bulkVerificationRunCall((client, path) =>
      client.post(path, body, { timeout: 120000 }),
    ),

  getBulkVerificationRun: (runId: string) =>
    bulkVerificationRunCall((client, path) =>
      client.get(`${path}/${encodeURIComponent(runId)}`),
    ),

  /** Answers `{ run: null }` when the database has no run in progress. */
  getActiveBulkVerificationRun: (databaseId: string) =>
    bulkVerificationRunCall((client, path) =>
      client.get(path, { params: { databaseId: databaseId } }),
    ),

  verifyWebsiteQuality: async (payload: any) =>
    (await initAxios(routeFor('verifyWebsiteQuality').baseUrl))
      ?.post(routeFor('verifyWebsiteQuality').path, payload, {
        timeout: 60000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.verifyWebsiteQuality failed', err);
        return { success: false, result: parseErrorMsg(err) };
      }),

  verifyDomainAge: async (payload: any) =>
    (await initAxios(routeFor('verifyDomainAge').baseUrl))
      ?.post(routeFor('verifyDomainAge').path, payload, {
        timeout: 30000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.verifyDomainAge failed', err);
        return { success: false, result: parseErrorMsg(err) };
      }),

  verifyDomainReputation: async (payload: any) =>
    (await initAxios(routeFor('verifyDomainReputation').baseUrl))
      ?.post(routeFor('verifyDomainReputation').path, payload, {
        timeout: 30000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.verifyDomainReputation failed', err);
        return { success: false, result: parseErrorMsg(err) };
      }),

  verifyEmail: async (payload: any) =>
    (await initAxios(routeFor('verifyEmail').baseUrl))
      ?.post(routeFor('verifyEmail').path, payload, {
        timeout: 30000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.verifyEmail failed', err);
        return { success: false, result: parseErrorMsg(err) };
      }),

  verifyPhone: async (payload: any) =>
    (await initAxios(routeFor('verifyPhone').baseUrl))
      ?.post(routeFor('verifyPhone').path, payload, {
        timeout: 30000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.verifyPhone failed', err);
        return { success: false, result: parseErrorMsg(err) };
      }),

  verifyBusinessLocation: async (payload: any) =>
    (await initAxios(routeFor('verifyBusinessLocation').baseUrl))
      ?.post(routeFor('verifyBusinessLocation').path, payload, {
        timeout: 30000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.verifyBusinessLocation failed', err);
        return { success: false, result: parseErrorMsg(err) };
      }),

  confirmBusinessLocationAspect: async (payload: {
    databaseEntityId: string;
    aspect: string;
    decision: 'confirmed' | 'denied';
  }) =>
    (await initAxios(routeFor('confirmBusinessLocationAspect').baseUrl))
      ?.post(routeFor('confirmBusinessLocationAspect').path, payload, {
        timeout: 30000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error(
          'EntityServiceAPI.confirmBusinessLocationAspect failed',
          err,
        );
        return { success: false, result: parseErrorMsg(err) };
      }),

  verifyCbrRegistry: async (payload: any) =>
    (await initAxios(routeFor('verifyCbrRegistry').baseUrl))
      ?.post(routeFor('verifyCbrRegistry').path, payload, {
        timeout: 30000,
      })
      .then((resp) => ({
        success: resp.status === 200,
        result: resp.data,
      }))
      .catch((err) => {
        console.error('EntityServiceAPI.verifyCbrRegistry failed', err);
        return { success: false, result: parseErrorMsg(err) };
      }),
};

export default EntityServiceAPI;
